import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, Mail, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { setCommessaFormat } from "@/lib/commessaCodice";
import { useTranslation } from "react-i18next";
import { APP_VERSION, APP_NAME, APP_BRAND } from "@/version";

// Util: pulizia totale cache PWA + storage + reload (force refresh)
async function _forceFullRefresh() {
  try {
    // 1. Cancella tutte le cache del browser per questa origin
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // 2. De-registra il Service Worker (la prossima volta verrà ri-registrato fresh)
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    // 3. Reload hard (ignora cache HTTP locale)
    toast.success("Cache svuotata, ricarico l'app…");
    setTimeout(() => window.location.reload(), 600);
  } catch (e) {
    console.error("forceFullRefresh failed:", e);
    toast.error("Errore nel reset cache: riprova chiudendo e riaprendo l'app");
  }
}

import { DailyTipsManager } from "@/components/DailyTipsManager";
import { CompanyLogoCard } from "@/components/CompanyLogoCard";
import { CapOttoOreCard } from "@/components/CapOttoOreCard";
import { BulkRemoveOffCard } from "@/components/BulkRemoveOffCard";
import { SpostaOreCard } from "@/components/SpostaOreCard";
export default function SettingsPage() {
  const { t } = useTranslation("app");
  const [settings, setSettings] = useState({
    email_recipients: [],
    auto_send_email: false,
    email_oggetto_template: "Rapportino N° {numero} del {data}",
    costo_orario_default: 20.0,
    overtime_warning_threshold: 13.0,
    home_recent_commesse_count: 10,
    global_hours_cap_enabled: false,
    notify_admin_on_unvidimato: true,
    display_uppercase: false,
    commessa_prefix: "EG",
    commessa_cifre_cliente: 2,
    commessa_cifre_numero: 3,
  });
  const [newEmail, setNewEmail] = useState("");
  const [maint, setMaint] = useState(null);
  const [showManual, setShowManual] = useState(false);
  useEffect(() => { api.get("/admin/maintenance/status").then(({ data }) => setMaint(data)).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);
  const origPrefixRef = useRef("EG");

  const load = async () => {
    const { data } = await api.get("/settings");
    origPrefixRef.current = (data.commessa_prefix || "EG").toUpperCase();
    setSettings({
      email_recipients: data.email_recipients || [],
      auto_send_email: !!data.auto_send_email,
      email_oggetto_template:
        data.email_oggetto_template || "Rapportino N° {numero} del {data}",
      costo_orario_default:
        data.costo_orario_default != null ? data.costo_orario_default : 20.0,
      overtime_warning_threshold:
        data.overtime_warning_threshold != null ? data.overtime_warning_threshold : 13.0,
      global_hours_cap_enabled: !!data.global_hours_cap_enabled,
      notify_admin_on_unvidimato:
        data.notify_admin_on_unvidimato == null ? true : !!data.notify_admin_on_unvidimato,
      display_uppercase: !!data.display_uppercase,
      commessa_prefix: data.commessa_prefix || "EG",
      commessa_cifre_cliente: data.commessa_cifre_cliente || 2,
      commessa_cifre_numero: data.commessa_cifre_numero || 3,
      last_export_date: data.last_export_date || "",
    });
  };

  useEffect(() => { load(); }, []);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!/^\S+@\S+\.\S+$/.test(e)) {
      toast.error("Email non valida");
      return;
    }
    if (settings.email_recipients.includes(e)) {
      toast.error("Email già presente");
      return;
    }
    setSettings({
      ...settings,
      email_recipients: [...settings.email_recipients, e],
    });
    setNewEmail("");
  };

  const removeEmail = (e) =>
    setSettings({
      ...settings,
      email_recipients: settings.email_recipients.filter((x) => x !== e),
    });

  const save = async () => {
    const newPrefix = (settings.commessa_prefix || "EG").trim().toUpperCase();
    if (newPrefix !== origPrefixRef.current) {
      const ok = window.confirm(
        t("set.prefixRenameConfirm", { old: origPrefixRef.current, new: newPrefix })
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", settings);
      setCommessaFormat({
        prefix: data.commessa_prefix,
        cifre_cliente: data.commessa_cifre_cliente,
        cifre_numero: data.commessa_cifre_numero,
      });
      if (data.prefix_rename) {
        origPrefixRef.current = data.prefix_rename.a;
        toast.success(
          t("set.prefixRenamed", {
            commesse: data.prefix_rename.commesse,
            rapportini: data.prefix_rename.rapportini,
            old: data.prefix_rename.da,
            new: data.prefix_rename.a,
          }),
          { duration: 8000 }
        );
      } else {
        toast.success(t("set.saved"));
      }
    } catch (e) {
      toast.error(t("set.saveErr"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
          {t("settings.kicker")}
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          {t("settings.title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {t("settings.sub")}
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 border rounded-md p-3">
          <div>
            <div className="font-bold text-sm flex items-center gap-2">
              <Mail className="h-4 w-4" /> {t("set.autoSend")}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {t("set.autoSendSub")}
            </div>
          </div>
          <Switch
            checked={settings.auto_send_email}
            onCheckedChange={(v) =>
              setSettings({ ...settings, auto_send_email: v })
            }
            data-testid="auto-send-switch"
          />
        </div>

        <div>
          <Label className="font-bold">{t("set.subject")}</Label>
          <Input
            value={settings.email_oggetto_template}
            onChange={(e) =>
              setSettings({ ...settings, email_oggetto_template: e.target.value })
            }
            placeholder="Rapportino N° {numero} del {data}"
            className="h-12 mt-1 font-mono text-sm"
            data-testid="email-subject-template-input"
          />
          <div className="text-xs text-slate-500 mt-1">
            {t("set.subjectVars")} <code>{"{numero}"}</code>, <code>{"{data}"}</code>
          </div>
        </div>

        <div>
          <Label className="font-bold">{t("set.recipients")}</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEmail()}
              placeholder="email@azienda.it"
              type="email"
              className="h-12 flex-1"
              data-testid="add-email-input"
            />
            <Button
              type="button"
              onClick={addEmail}
              variant="outline"
              className="h-12"
              data-testid="add-email-button"
            >
              <Plus className="h-4 w-4 mr-1" /> {t("set.add")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {settings.email_recipients.length === 0 ? (
              <div className="text-sm text-slate-500">
                {t("set.noRecipients")}
              </div>
            ) : (
              settings.email_recipients.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-2 bg-slate-100 px-3 py-1 rounded-full text-sm font-mono"
                  data-testid={`email-recipient-${e}`}
                >
                  {e}
                  <button
                    onClick={() => removeEmail(e)}
                    className="text-red-600 hover:text-red-800"
                    aria-label={`Rimuovi ${e}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3 border-indigo-200">
        <div className="font-heading font-bold tracking-tight">
          {t("set.formatoTitle")}
        </div>
        <p className="text-sm text-slate-600">
          Personalizza lo schema dei codici commessa:{" "}
          <strong>SIGLA-CLIENTE-NUMERO</strong> (+ suffisso <code>-OFF</code>{" "}
          per l&apos;officina). La modifica vale solo per i{" "}
          <strong>nuovi codici</strong>: quelli esistenti restano validi.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
          <div>
            <div className="text-xs font-bold text-slate-500 mb-1">
              Sigla iniziale (1–5 lettere)
            </div>
            <Input
              value={settings.commessa_prefix}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commessa_prefix: e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z]/g, "")
                    .slice(0, 5),
                })
              }
              className="h-12 font-mono font-bold uppercase"
              data-testid="commessa-prefix-input"
            />
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 mb-1">
              Cifre codice cliente (1–4)
            </div>
            <Input
              type="number"
              min="1"
              max="4"
              value={settings.commessa_cifre_cliente}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commessa_cifre_cliente: parseInt(e.target.value) || 2,
                })
              }
              className="h-12 font-mono font-bold"
              data-testid="commessa-cifre-cliente-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              max{" "}
              {Math.pow(10, Math.min(Math.max(settings.commessa_cifre_cliente || 2, 1), 4)) - 1}{" "}
              clienti
            </div>
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 mb-1">
              Cifre numero commessa (2–5)
            </div>
            <Input
              type="number"
              min="2"
              max="5"
              value={settings.commessa_cifre_numero}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commessa_cifre_numero: parseInt(e.target.value) || 3,
                })
              }
              className="h-12 font-mono font-bold"
              data-testid="commessa-cifre-numero-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              max{" "}
              {Math.pow(10, Math.min(Math.max(settings.commessa_cifre_numero || 3, 2), 5)) - 1}{" "}
              commesse per cliente
            </div>
          </div>
        </div>
        <div
          className="text-sm bg-indigo-50 border border-indigo-200 rounded-md p-2.5 font-mono font-bold text-indigo-800"
          data-testid="commessa-formato-preview"
        >
          Anteprima:{" "}
          {(settings.commessa_prefix || "EG")}
          -{"X".repeat(Math.min(Math.max(settings.commessa_cifre_cliente || 2, 1), 4))}
          -{"X".repeat(Math.min(Math.max(settings.commessa_cifre_numero || 3, 2), 5))}
          {"  "}(officina:{" "}
          {(settings.commessa_prefix || "EG")}
          -{"X".repeat(Math.min(Math.max(settings.commessa_cifre_cliente || 2, 1), 4))}
          -{"X".repeat(Math.min(Math.max(settings.commessa_cifre_numero || 3, 2), 5))}
          -OFF)
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-heading font-bold tracking-tight">
          {t("set.exportTitle")}
        </div>
        <p className="text-sm text-slate-600">
          {t("set.exportSub")}
        </p>
        <div className="flex items-center gap-3 max-w-sm">
          <span className="text-sm text-slate-500">€</span>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={settings.costo_orario_default}
            onChange={(e) =>
              setSettings({
                ...settings,
                costo_orario_default: parseFloat(e.target.value) || 0,
              })
            }
            className="h-12 text-lg font-mono font-bold"
            data-testid="costo-orario-input"
          />
          <span className="text-sm text-slate-500">/ora</span>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-heading font-bold tracking-tight">
          {t("set.overtimeTitle")}
        </div>
        <p className="text-sm text-slate-600">
          {t("set.overtimeSub")}
        </p>
        <div className="flex items-center gap-3 max-w-sm">
          <Input
            type="number"
            min="1"
            step="0.5"
            value={settings.overtime_warning_threshold}
            onChange={(e) =>
              setSettings({
                ...settings,
                overtime_warning_threshold: parseFloat(e.target.value) || 0,
              })
            }
            className="h-12 text-lg font-mono font-bold"
            data-testid="overtime-threshold-input"
          />
          <span className="text-sm text-slate-500">{t("set.orePerGiorno")}</span>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-heading font-bold tracking-tight">
          {t("set.recentTitle")}
        </div>
        <p className="text-sm text-slate-600">
          {t("set.recentSub")}
        </p>
        <div className="flex items-center gap-3 max-w-sm">
          <Input
            type="number"
            min="1"
            max="50"
            step="1"
            value={settings.home_recent_commesse_count ?? 10}
            onChange={(e) =>
              setSettings({
                ...settings,
                home_recent_commesse_count: parseInt(e.target.value, 10) || 10,
              })
            }
            className="h-12 text-lg font-mono font-bold"
            data-testid="home-recent-commesse-count-input"
          />
          <span className="text-sm text-slate-500">{t("set.commesseUnit")}</span>
        </div>
      </Card>

      <CapOttoOreCard settings={settings} setSettings={setSettings} />

      <BulkRemoveOffCard />

      <SpostaOreCard />

      <Card className="p-5 space-y-3 border-emerald-200">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-heading font-bold tracking-tight flex items-center gap-2">
              {t("set.notifyTitle")}
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {t("set.notifySub")}
            </p>
          </div>
          <Switch
            checked={settings.notify_admin_on_unvidimato}
            onCheckedChange={(v) =>
              setSettings({ ...settings, notify_admin_on_unvidimato: v })
            }
            data-testid="notify-admin-unvidimato-switch"
          />
        </div>
      </Card>

      {/* Iter 80: logo azienda */}
      <CompanyLogoCard />

      {/* Iter 76: gestione frasi del giorno */}
      <DailyTipsManager />

      {/* Iter 30w: visualizzazione UPPERCASE — solo display, non modifica i dati */}
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="font-heading font-bold tracking-tight">
              {t("set.upperTitle")}
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {t("set.upperSub")}
            </p>
          </div>
          <Switch
            checked={settings.display_uppercase}
            onCheckedChange={(v) =>
              setSettings({ ...settings, display_uppercase: v })
            }
            data-testid="display-uppercase-switch"
          />
        </div>
      </Card>

      <Card className="p-5 space-y-3 border-emerald-300 bg-emerald-50" data-testid="auto-maintenance-card">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading font-bold tracking-tight text-emerald-900">Manutenzione automatica attiva</div>
            <p className="text-sm text-emerald-800 mt-1">
              Ogni {maint?.every_hours || 6} ore (e a ogni avvio) l'app controlla da sola codici commessa in maiuscolo, nomi dei dipendenti nei rapportini,
              famiglie EG-XX e commesse madre/figlia: se trova un'anomalia la corregge senza bisogno di premere nulla.
            </p>
            <div className="text-xs text-emerald-900 mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="auto-maintenance-status">
              <span>Ultimo controllo: <b>{maint?.last_run ? new Date(maint.last_run).toLocaleString("it-IT") : "in attesa del primo giro"}</b></span>
              <span>Ultimo esito: <b>{maint?.last_result?.error ? "errore" : `${maint?.last_result?.interventi ?? 0} correzioni`}</b></span>
              <span>Correzioni totali: <b>{maint?.interventi_totali ?? 0}</b></span>
            </div>
            <button type="button" onClick={() => setShowManual((v) => !v)} className="mt-3 text-xs font-bold text-emerald-900 underline" data-testid="toggle-manual-maintenance">
              {showManual ? "Nascondi strumenti manuali" : "Strumenti manuali (di norma non servono)"}
            </button>
          </div>
        </div>
      </Card>

      {showManual && (<>
      <Card className="p-5 space-y-3 border-slate-300 bg-slate-50">
        <div className="flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-slate-600 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading font-bold tracking-tight">
              Sistemazione codici commessa (tutto in maiuscolo)
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Trasforma in <strong>MAIUSCOLO</strong> tutti i codici commessa,
              sia nell&apos;elenco commesse sia nelle righe dei rapportini già
              salvati. Serve a evitare doppioni scritti in modo diverso (es.{" "}
              <code>EG-13-167-OFF</code> e <code>EG-13-167-off</code>).
            </p>
            <Button
              variant="outline"
              className="mt-3"
              data-testid="normalize-case-button"
              onClick={async () => {
                try {
                  const { data } = await api.post(
                    "/admin/commesse/normalize-codici-case"
                  );
                  const tot =
                    (data?.commesse_aggiornate || 0) +
                    (data?.righe_aggiornate || 0);
                  if (tot > 0) {
                    toast.success(
                      `Normalizzate ${data.commesse_aggiornate} commesse e ${data.righe_aggiornate} righe rapportini.`
                    );
                  } else {
                    toast.success("Tutti i codici sono già in UPPERCASE ✨");
                  }
                } catch (_) {
                  toast.error("Normalizzazione case non riuscita");
                }
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Forza UPPERCASE ora
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3 border-slate-300 bg-slate-50">
        <div className="flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-slate-600 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading font-bold tracking-tight">
              Normalizza nomi dipendenti nei rapportini
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Scansiona tutti i rapportini storici e riassegna i nomi dei
              dipendenti scritti in modi diversi (es. <code>BENEDETTI FLAVIO</code>{" "}
              → <code>Benedetti Vallenari Flavio</code>) al record canonico
              dell&apos;anagrafica. Esegui dopo un import massivo o quando vedi
              dipendenti &quot;doppi&quot; nei riepiloghi.
            </p>
            <Button
              variant="outline"
              className="mt-3"
              data-testid="normalize-employees-button"
              onClick={async () => {
                try {
                  const { data } = await api.post(
                    "/admin/rapportini/normalize-employees"
                  );
                  if (data?.righe_aggiornate > 0) {
                    toast.success(
                      `Normalizzate ${data.righe_aggiornate} righe in ${data.rapportini_aggiornati} rapportini.`
                    );
                  } else {
                    toast.success("Tutti i nomi sono già normalizzati ✨");
                  }
                } catch (_) {
                  toast.error("Normalizzazione non riuscita");
                }
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Normalizza nomi ora
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3 border-slate-300 bg-slate-50">
        <div className="flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-slate-600 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading font-bold tracking-tight">
              Sincronizza famiglie commesse (EG-XX-)
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Tutte le commesse <strong>con lo stesso prefisso EG-XX-</strong>{" "}
              (es. <code>EG-13-167</code> e <code>EG-13-300</code>) appartengono
              allo stesso cliente. Premi qui per allineare retroattivamente il
              cliente più frequente di ogni famiglia a tutte le sue sorelle. La
              sincronizzazione automatica avviene comunque ad ogni salvataggio
              di una commessa.
            </p>
            <Button
              variant="outline"
              className="mt-3"
              data-testid="resync-famiglie-button"
              onClick={async () => {
                try {
                  const { data } = await api.post(
                    "/admin/commesse/famiglie-resync"
                  );
                  if (data?.totale_commesse_aggiornate > 0) {
                    toast.success(
                      `Sincronizzate ${data.totale_commesse_aggiornate} commesse in ${data.famiglie_aggiornate} famigli${data.famiglie_aggiornate === 1 ? "a" : "e"}.`
                    );
                  } else {
                    toast.success("Tutte le famiglie sono già sincronizzate.");
                  }
                } catch (_) {
                  toast.error("Sincronizzazione famiglie non riuscita");
                }
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sincronizza famiglie ora
            </Button>
          </div>
        </div>
      </Card>

      </>)}

      <Card className="p-5 space-y-3 border-slate-300 bg-slate-50">
        <div className="flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-slate-600 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading font-bold tracking-tight">
              {t("set.maintTitle")}
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {t("set.maintSub")}
            </p>
            <Button
              variant="outline"
              onClick={_forceFullRefresh}
              data-testid="force-refresh-button"
              className="h-11 mt-3 border-slate-400 hover:bg-slate-100"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("set.maintBtn")}
            </Button>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-settings-button"
          className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
        >
          <Save className="h-5 w-5 mr-2" /> {t("set.saveBtn")}
        </Button>
      </div>

      {/* Iter 30u: footer versione app */}
      <div
        className="pt-4 text-center text-[11px] text-slate-400 select-none"
        data-testid="app-version-footer"
      >
        {APP_NAME} · v{APP_VERSION} · {APP_BRAND}
        {settings?.last_export_date && (
          <span className="block mt-0.5">
            {t("set.lastExportFooter")} {settings.last_export_date}
          </span>
        )}
      </div>
    </div>
  );
}

